import { smartReplyAriaText, smartReplyTooltip } from './smartReplyButton.locstring.json';
import updateSIGSDataOnClickAction from '../actions/updateSIGSDataOnClickAction';
import loc, { format } from 'owa-localize';
import { ComposeViewState } from 'owa-mail-compose-store';
import { lazyReplyWithText, ReplyWithTextFeatures } from 'owa-mail-message-actions';
import { FeatureNames } from 'owa-mail-smart-pill-features';
import { ClientItem, SmartReplySuggestion, SmartReplySuggestionType } from 'owa-mail-store';
import {
    initializeSmartPillLogData,
    SmartPillData,
    SmartPillLogData,
    logOnClickDatapoint,
    logOnClickDatapointLegacy,
    SMART_REPLY_MENU_CLICKED,
} from 'owa-smart-pill';
import { IContextualMenuItem, IContextualMenuProps } from '@fluentui/react/lib/ContextualMenu';
import { isFeatureEnabled } from 'owa-feature-flags';
import { ControlIcons } from 'owa-control-icons';

const styles = require('./SmartReplyPill.scss');

export default function createSmartReplyPillData(
    item: ClientItem,
    smartReply: SmartReplySuggestion,
    loggableSmartReply: string,
    extractionId: string,
    index: number
): SmartPillData {
    const logData: SmartPillLogData = initializeSmartPillLogData(
        FeatureNames.SmartReply,
        loggableSmartReply,
        extractionId,
        index + 1 /* confidence score */
    );

    let smartReplyParentButtonText: string = null;
    let menuProps: IContextualMenuProps = null;

    if (smartReply.type == SmartReplySuggestionType.Simple) {
        smartReplyParentButtonText = smartReply.suggestions as string;
    } else if (smartReply.type == SmartReplySuggestionType.Group) {
        switch (smartReply.suggestions.length) {
            case 0:
                smartReplyParentButtonText = '';
                break;
            case 1:
                smartReplyParentButtonText = smartReply.suggestions[0];
                break;
            default:
                smartReplyParentButtonText = smartReply.suggestions[0];
                menuProps = {
                    items: buildMenuItemForSmartReplyAlternateSuggestions(
                        item,
                        smartReply,
                        logData
                    ),
                };
        }
    }

    return {
        ariaLabel: format(loc(smartReplyAriaText), smartReplyParentButtonText),
        featureName: FeatureNames.SmartReply,
        icon: isFeatureEnabled('mc-smartPillsDesignV2') ? { iconName: ControlIcons.Edit } : null,
        onClick: async (pill: SmartPillData) => {
            // Open inline compose and insert text.
            const composeViewState: ComposeViewState = await lazyReplyWithText.importAndExecute(
                item.ItemId.Id,
                item.ConversationId.Id,
                smartReplyParentButtonText,
                ReplyWithTextFeatures.SmartReply
            );

            // Update the composeViewState with some data relevant for SIGS logging.
            updateSIGSDataOnClickAction(
                composeViewState,
                pill.logData.mainPayload.timeClicked,
                pill.logData.mainPayload.position
            );
        },
        logData: logData,
        index: index,
        text: smartReplyParentButtonText,
        toolTip: isFeatureEnabled('mc-smartPillsTooltipV2')
            ? loc(smartReplyTooltip)
            : smartReplyParentButtonText,
        confidenceScore: logData.mainPayload.confidenceScore,
        menuProps: menuProps,
    };
}

export function buildMenuItemForSmartReplyAlternateSuggestions(
    item: ClientItem,
    smartReply: SmartReplySuggestion,
    logData: SmartPillLogData
): IContextualMenuItem[] {
    const suggestions: string[] = smartReply.suggestions as string[];
    const menuItems: IContextualMenuItem[] = suggestions.slice(1).map(value => {
        return {
            key: value,
            name: value,
            ariaLabel: value,
            title: isFeatureEnabled('mc-smartPillsTooltipV2') ? loc(smartReplyTooltip) : value,
            itemProps: {
                styles: { root: styles.menuButton },
            },
            onClick: () => {
                logOnClickDatapoint(SMART_REPLY_MENU_CLICKED, logData, { subtype: value });

                // For now, just log a legacy datapoint so that the existing smart reply scorecards are not broken.
                // However, we do need a new scorecard for the newer datapoints added.
                logOnClickDatapointLegacy(
                    logData.mainPayload.EID,
                    logData.mainPayload.position,
                    logData.mainPayload.loggableText,
                    FeatureNames.SmartReply,
                    logData.mainPayload.language,
                    logData.mainPayload.locale,
                    logData.cosmosOnlyData,
                    value
                );

                // Open inline compose and insert text.
                lazyReplyWithText.importAndExecute(
                    item.ItemId.Id,
                    item.ConversationId.Id,
                    value,
                    ReplyWithTextFeatures.SmartReply
                );
            },
        };
    });
    return menuItems;
}
